import { Box } from "@mantine/core";
import {
  IconBuilding,
  IconMessageCircle,
  IconFolder,
  IconShield,
  IconFileText,
  IconCheck,
} from "@tabler/icons-react";

const NAVY = "#04305c";
const GOLD = "#EAB308";

type CollaborationUnlockIllustrationProps = {
  /** Smaller layout for phones / narrow modals */
  compact?: boolean;
};

/** Decorative graphic for the Unlock Full Access payment modal (right column). */
export default function CollaborationUnlockIllustration({ compact = false }: CollaborationUnlockIllustrationProps) {
  const maxWidth = compact ? 200 : 280;
  const hubSize = compact ? 88 : 112;
  const orbitSize = compact ? 50 : 64;
  const centerCheck = compact ? 30 : 36;
  const satellite = compact ? 32 : 40;
  const buildingIcon = compact ? 22 : 28;
  const satelliteIcon = compact ? 15 : 18;
  const checkIcon = compact ? 16 : 20;

  return (
    <Box
      style={{
        position: "relative",
        width: "100%",
        maxWidth,
        aspectRatio: "1",
        margin: "0 auto",
      }}
    >
      <Box
        style={{
          position: "absolute",
          inset: "8%",
          borderRadius: "50%",
          border: "1px dashed #BFDBFE",
          opacity: 0.6,
        }}
      />
      <Box
        style={{
          position: "absolute",
          inset: "18%",
          borderRadius: "50%",
          border: "1px dashed #BFDBFE",
          opacity: 0.4,
        }}
      />
      <Box
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: hubSize,
          height: hubSize,
        }}
      >
        <Box
          style={{
            position: "absolute",
            left: 0,
            top: compact ? 8 : 12,
            width: orbitSize,
            height: orbitSize,
            borderRadius: "50%",
            background: "#DBEAFE",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid #93C5FD",
          }}
        >
          <IconBuilding size={buildingIcon} color="#2563EB" stroke={1.5} />
        </Box>
        <Box
          style={{
            position: "absolute",
            right: 0,
            top: compact ? 8 : 12,
            width: orbitSize,
            height: orbitSize,
            borderRadius: "50%",
            background: "#FEF9C3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${GOLD}`,
          }}
        >
          <IconBuilding size={buildingIcon} color={GOLD} stroke={1.5} />
        </Box>
        <Box
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: centerCheck,
            height: centerCheck,
            borderRadius: "50%",
            background: NAVY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(4, 48, 92, 0.35)",
            zIndex: 2,
          }}
        >
          <IconCheck size={checkIcon} color="#fff" stroke={2.5} />
        </Box>
      </Box>
      {(
        [
          { Icon: IconMessageCircle, top: "4%", left: "50%", transform: "translateX(-50%)" },
          { Icon: IconFolder, top: "50%", right: "2%", transform: "translateY(-50%)" },
          { Icon: IconShield, bottom: "4%", left: "50%", transform: "translateX(-50%)" },
          { Icon: IconFileText, top: "50%", left: "2%", transform: "translateY(-50%)" },
        ] satisfies Array<{
          Icon: typeof IconMessageCircle;
          top?: string;
          left?: string;
          right?: string;
          bottom?: string;
          transform: string;
        }>
      ).map(({ Icon, top, left, right, bottom, transform }, i) => (
        <Box
          key={i}
          style={{
            position: "absolute",
            top,
            left,
            right,
            bottom,
            transform,
            width: satellite,
            height: satellite,
            borderRadius: "50%",
            background: "#fff",
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 4px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Icon size={satelliteIcon} color={NAVY} stroke={1.5} />
        </Box>
      ))}
    </Box>
  );
}
